import TagManager from 'react-gtm-module';
import { BaseGym } from '../types/Gym';

const PRODUCT_TYPE_DAYPASS = 'daypass';
const PRODUCT_TYPE_FITFIX5 = '5passes';
const PRODUCT_TYPE_FITFIX10 = '10passes';
const PRODUCT_TYPE_MONTHLY = 'monthly';
const PRODUCT_TYPE_DIRECT = 'direct';
const PRODUCT_NAME_DAYPASS = '1 Day Pass';
const PRODUCT_NAME_FITFIX5 = 'FitFix 5';
const PRODUCT_NAME_FITFIX10 = 'FitFix 10';
const PRODUCT_NAME_MONTHLY = 'Monthly+';
const PRODUCT_NAME_DIRECT = 'Direct Membership';
const VENUE_DETAIL_PAGE = 'Venue Detail Page';

type ProductProps = {
  name: string;
  id: string;
  price: string;
  brand: string;
  category: string;
  variant: string;
  position: number;
};

type BasketProps = {
  quantity: number;
};

type ImpressionProps = {
  list: string;
};

type ProductImpressionProps = ProductProps & ImpressionProps;

type BasketProductProps = ProductProps & BasketProps;

type GymProductProps = {
  id: string;
  price: number;
  type: string;
};

const fullProductName = (productType: string) => {
  switch (productType) {
    case `${PRODUCT_TYPE_DIRECT}`:
      return `${PRODUCT_NAME_DIRECT}`;
    case `${PRODUCT_TYPE_MONTHLY}`:
      return `${PRODUCT_NAME_MONTHLY}`;
    case `${PRODUCT_TYPE_FITFIX5}`:
      return `${PRODUCT_NAME_FITFIX5}`;
    case `${PRODUCT_TYPE_FITFIX10}`:
      return `${PRODUCT_NAME_FITFIX10}`;
    case `${PRODUCT_TYPE_DAYPASS}`:
      return `${PRODUCT_NAME_DAYPASS}`;
  }
};

const includedProducts = (recommendedPass: string | null): string[] => {
  return recommendedPass
    ? recommendedPass === PRODUCT_TYPE_DIRECT
      ? [PRODUCT_TYPE_DIRECT, PRODUCT_TYPE_MONTHLY, PRODUCT_TYPE_DAYPASS]
      : [PRODUCT_TYPE_MONTHLY, PRODUCT_TYPE_DAYPASS]
    : [PRODUCT_TYPE_DAYPASS, PRODUCT_TYPE_FITFIX5, PRODUCT_TYPE_FITFIX10, PRODUCT_TYPE_MONTHLY];
};

const trackProductImpressions = (gym: BaseGym, recommendedPass: string | null): void => {
  const impressions: Array<ProductImpressionProps> = [gymToImpressions(gym, recommendedPass)];

  TagManager.dataLayer({
    dataLayer: {
      ecommerce: { impressions },
    },
  });
};

const gymToProducts = (gym: BaseGym, includedProducts: Array<string>) => {
  const products = gym.products.filter((product: GymProductProps) => includedProducts.includes(product.type));

  return products.map((product: GymProductProps, idx) => ({
    name: gym.name,
    id: gym.id,
    price: (product.price / 100).toString(10),
    brand: gym.business.name,
    category: gym.location.locality,
    variant: `${fullProductName(product.type)}`,
    position: idx,
  }));
};

const gymToImpressions = (gym: BaseGym, recommendedPass: string | null): any => {
  const products = gymToProducts(gym, includedProducts(recommendedPass));

  return products.map((product: ProductProps) =>
    Object.assign(product, {
      list: VENUE_DETAIL_PAGE,
    }),
  );
};

const trackAddToCart = (gym: BaseGym, productType: string): void => {
  const products: Array<BasketProductProps> = gymToProducts(gym, [productType]).map((product: ProductProps) =>
    Object.assign(product, {
      quantity: 1,
    }),
  );
  TagManager.dataLayer({
    dataLayer: {
      event: 'addToCart',
      ecommerce: {
        add: {
          products,
        },
      },
    },
  });
};

export { trackProductImpressions, trackAddToCart };
